import React, { useState } from "react";
import useSWR from "swr";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { KeyRound, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Mirrors the server's password policy so problems show before submitting
function passwordProblem(pw) {
  if (pw.length < 12) return "At least 12 characters";
  if (!/[A-Z]/.test(pw)) return "Add an uppercase letter";
  if (!/[a-z]/.test(pw)) return "Add a lowercase letter";
  if (!/\d/.test(pw)) return "Add a number";
  if (!/[^A-Za-z0-9]/.test(pw)) return "Add a symbol";
  return null;
}

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function UserManagement() {
  const { data: users, mutate } = useSWR("/api/admin/users", fetcher);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ email: "", name: "", password: "", role: "viewer" });
  const { user: me } = useAuth() || {};
  const [pwUser, setPwUser] = useState(null);
  const [pw, setPw] = useState({ next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const openSetPassword = (u) => {
    setPw({ next: "", confirm: "" });
    setPwUser(u);
  };

  const savePassword = async () => {
    setPwSaving(true);
    try {
      await api.patch(`/api/admin/users/${pwUser.id}`, { password: pw.next });
      const self = me && (me.id === pwUser.id || me.email === pwUser.email);
      toast.success(self
        ? "Your password was updated. Other sessions have been signed out."
        : `Password updated for ${pwUser.email}. They'll need to sign in again with the new password.`);
      setPwUser(null);
      mutate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPwSaving(false);
    }
  };

  const pwIssue = pw.next ? passwordProblem(pw.next) : null;
  const pwMismatch = pw.confirm && pw.next !== pw.confirm;
  const pwReady = pw.next && !pwIssue && pw.next === pw.confirm;

  const create = async () => {
    try {
      await api.post("/api/admin/users", draft);
      toast.success("User created");
      setOpen(false);
      setDraft({ email: "", name: "", password: "", role: "viewer" });
      mutate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/api/admin/users/${u.id}`, { active: !u.active });
      mutate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const setRole = async (u, role) => {
    try {
      await api.patch(`/api/admin/users/${u.id}`, { role });
      mutate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-stone-500">Access control</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tighter text-stone-900 mt-1">
            Manage users
          </h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand hover:bg-brand-hover text-white" data-testid="user-add-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New user
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Provision a user</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Email</Label>
                <Input type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} data-testid="user-add-email" />
              </div>
              <div>
                <Label>Name</Label>
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="user-add-name" />
              </div>
              <div>
                <Label>Password (min 12 chars, upper+lower+number+symbol)</Label>
                <Input type="password" value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} data-testid="user-add-password" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={draft.role} onValueChange={(v) => setDraft({ ...draft, role: v })}>
                  <SelectTrigger data-testid="user-add-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="analyst">Analyst</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-brand hover:bg-brand-hover text-white" onClick={create} data-testid="user-add-submit">
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500">
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Last login</th>
              <th className="px-4 py-3">Password set</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(users || []).map((u) => (
              <tr key={u.id} className="border-b border-stone-100 last:border-0" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                    <SelectTrigger className="w-32 h-8 text-xs" data-testid={`user-role-${u.email}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">admin</SelectItem>
                      <SelectItem value="analyst">analyst</SelectItem>
                      <SelectItem value="viewer">viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <Badge className={u.active ? "bg-brand-good text-white" : "bg-stone-300 text-stone-700"}>
                    {u.active ? "active" : "disabled"}
                  </Badge>
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-stone-500">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3 text-[11px] text-stone-500" data-testid={`user-pw-updated-${u.email}`}>
                  {u.password_updated_at ? (
                    <>
                      <div className="font-mono">{new Date(u.password_updated_at).toLocaleString()}</div>
                      {u.password_updated_by && <div>by {u.password_updated_by}</div>}
                    </>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <Button variant="outline" size="sm" className="h-8 text-xs"
                            onClick={() => openSetPassword(u)} data-testid={`user-set-password-${u.email}`}>
                      <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Set password
                    </Button>
                    <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`user-toggle-${u.email}`} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-stone-600">
              For <span className="font-mono">{pwUser?.email}</span>
              {pwUser?.name ? ` (${pwUser.name})` : ""}. They will be signed out everywhere
              and must sign in with the new password.
            </div>
            <div>
              <Label>New password</Label>
              <Input type="password" autoComplete="new-password" value={pw.next}
                     onChange={(e) => setPw({ ...pw, next: e.target.value })}
                     data-testid="user-set-password-new" />
              <div className={pwIssue ? "text-xs text-brand-bad mt-1" : "text-xs text-stone-500 mt-1"}>
                {pwIssue || "Min 12 characters with upper case, lower case, a number and a symbol."}
              </div>
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input type="password" autoComplete="new-password" value={pw.confirm}
                     onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                     data-testid="user-set-password-confirm" />
              {pwMismatch && <div className="text-xs text-brand-bad mt-1">Passwords don't match</div>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwUser(null)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-hover text-white" onClick={savePassword}
                    disabled={!pwReady || pwSaving} data-testid="user-set-password-submit">
              {pwSaving ? "Saving…" : "Set password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
